import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";

const PLAN_NAMES = {
  starter_monthly: "Starter", growth_monthly: "Growth", pro_monthly: "Pro",
  business_monthly: "Business AI", agency_monthly: "Agency",
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [plan, setPlan] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setPlan(PLAN_NAMES[r.data.lookup_key] || "");
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (attempts >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="min-h-screen bg-background grid-noise flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 text-center animate-float-up">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-5" />
            <h1 className="font-display text-xl font-bold mb-2" data-testid="payment-checking">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground">Please wait while we verify with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-accent/15 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8 text-accent" /></div>
            <h1 className="font-display text-2xl font-bold mb-2" data-testid="payment-success">Subscription Active 🎉</h1>
            <p className="text-sm text-muted-foreground mb-6">{plan ? `Welcome to AdsBoster ${plan}. ` : ""}Your plan is now active.</p>
            <Button data-testid="payment-goto-dashboard" onClick={() => navigate("/dashboard")} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl w-full">
              <Zap className="w-4 h-4 mr-1" fill="currentColor" /> Go to Command Centre
            </Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-destructive/15 flex items-center justify-center mx-auto mb-5"><XCircle className="w-8 h-8 text-destructive" /></div>
            <h1 className="font-display text-xl font-bold mb-2" data-testid="payment-failed">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-sm text-muted-foreground mb-6">{state === "timeout" ? "Your payment is taking longer than expected. Check back shortly." : "We couldn't confirm your payment."}</p>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-xl w-full" data-testid="payment-back-home">Back to Home</Button>
          </>
        )}
      </div>
    </div>
  );
}
